import clsx from 'clsx';
import Link, { type LinkProps as NextLinkProps } from 'next/link';
import type React from 'react';
import { Count } from '../Count';
import styles from './FilterButton.module.scss';

interface SharedFilterButtonProps {
  children: React.ReactNode;
  title?: string;
  isActive?: boolean;
  count?: string;
  countAriaLabel?: string;
  testId?: string;
}

type FilterButtonProps = SharedFilterButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
  };

type FilterLinkProps = SharedFilterButtonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Pick<NextLinkProps, 'replace' | 'scroll' | 'prefetch' | 'locale'> & {
    href: string;
  };

export type FilterButtonComponentProps = FilterButtonProps | FilterLinkProps;

export const FilterButton = (props: FilterButtonComponentProps) => {
  const { children, title, isActive, count, countAriaLabel, testId, ...rest } = props;

  const content = (
    <>
      {children}
      {count && (
        <Count aria-label={countAriaLabel} className={styles.count}>
          {count}
        </Count>
      )}
    </>
  );

  if ('href' in props && props.href) {
    const {
      href,
      replace,
      scroll,
      prefetch,
      locale,
      title: _,
      ...anchorProps
    } = rest as FilterLinkProps;
    return (
      <Link
        href={href}
        replace={replace}
        scroll={scroll}
        prefetch={prefetch}
        locale={locale}
        className={clsx(styles.button, {
          [styles.activeBackground]: isActive,
        })}
        title={title}
        data-testid={testId}
        {...anchorProps}
      >
        {content}
      </Link>
    );
  }

  const { title: _, ...buttonProps } = rest as FilterButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      className={clsx(styles.button, {
        [styles.activeBackground]: isActive,
      })}
      title={title}
      data-testid={testId}
      {...buttonProps}
    >
      {content}
    </button>
  );
};
